import React, { useState, useMemo, useEffect } from 'react';
import { System, Line } from '../types';
import Pagination from './Pagination';
import { ITEMS_PER_PAGE, PencilIcon, TrashIcon } from '../constants';
import { useDatabaseContext } from '../contexts/DatabaseContext';

interface SystemTableProps {
  systems: System[];
  onModify: (system: System) => void;
  onDelete: (system: System) => void;
}

const SystemTable: React.FC<SystemTableProps> = ({ systems, onModify, onDelete }) => {
  const { apiUrl } = useDatabaseContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [allLines, setAllLines] = useState<Line[]>([]);
  const totalPages = Math.ceil(systems.length / ITEMS_PER_PAGE);

  // Fetch all lines to display line names
  useEffect(() => {
    const fetchLines = async () => {
      try {
        const response = await fetch(`${apiUrl}/lines`);
        if (response.ok) {
          const data = await response.json();
          setAllLines(data.data || []);
        }
      } catch (error) {
        console.error('Error fetching lines:', error);
      }
    };
    fetchLines();
  }, [apiUrl]);

  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const lastPageIndex = firstPageIndex + ITEMS_PER_PAGE;
    return systems.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, systems]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getLineNames = (lineIds: string[]) => {
    if (!lineIds || lineIds.length === 0) return [];
    return lineIds.map(id => allLines.find(line => line.id === id)).filter(Boolean) as Line[];
  };

  if (systems.length === 0) {
    return <p className="text-slate-600 italic text-center py-8">No systems configured.</p>;
  }

  return (
    <div className="bg-white p-4 md:p-6 rounded-xl shadow-lg">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm text-left text-slate-600">
          <thead className="text-xs text-slate-500 uppercase bg-slate-50">
            <tr>
              <th scope="col" className="px-4 py-3">System Name</th>
              <th scope="col" className="px-4 py-3">Assigned Lines</th>
              <th scope="col" className="px-4 py-3">Description</th>
              <th scope="col" className="px-4 py-3 text-center">Visibility</th>
              <th scope="col" className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentTableData.map((system) => (
              <tr key={system.id} className="bg-white border-b border-slate-200 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{system.name}</td>
                <td className="px-4 py-3">
                  {(() => {
                    const assignedLines = getLineNames(system.lineIds || []);
                    if (assignedLines.length === 0) {
                      return <span className="text-slate-400 italic">No lines assigned</span>;
                    }
                    return (
                      <div className="space-y-1">
                        {assignedLines.map((line, index) => (
                          <div key={line.id} className="flex items-center space-x-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {line.name}
                            </span>
                            {line.hardwareId && (
                              <span className="text-xs text-slate-500">HW ID: {line.hardwareId}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()} 
                </td>
                <td className="px-4 py-3">
                  {system.description ? (
                    <span className="text-slate-600">{system.description}</span>
                  ) : (
                    <span className="text-slate-400 italic">No description</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex justify-center flex-wrap gap-1">
                    <span 
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        system.showOnDashboard ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                      title={system.showOnDashboard ? 'Visible on Dashboard' : 'Hidden from Dashboard'}
                    >
                      Dashboard
                    </span>
                    <span 
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        system.showOnStations ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                      }`}
                      title={system.showOnStations ? 'Visible on Stations' : 'Hidden from Stations'}
                    >
                      Stations
                    </span>
                    <span 
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        system.showOnReports ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'
                      }`}
                      title={system.showOnReports ? 'Visible on Reports' : 'Hidden from Reports'}
                    >
                      Reports
                    </span>
                    <span 
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        system.showOnModes ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-600'
                      }`}
                      title={system.showOnModes ? 'Visible on Modes' : 'Hidden from Modes'}
                    >
                      Modes
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex justify-center space-x-3">
                    <button
                      onClick={() => onModify(system)}
                      className="p-3 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 rounded-lg border border-indigo-200 hover:border-indigo-300 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                      style={{ minHeight: '44px', minWidth: '44px' }}
                      title="Edit System"
                      aria-label={`Edit system ${system.name}`}
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => onDelete(system)}
                      className="p-3 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-lg border border-red-200 hover:border-red-300 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                      style={{ minHeight: '44px', minWidth: '44px' }}
                      title="Delete System"
                      aria-label={`Delete system ${system.name}`}
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default SystemTable;